import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from './Avatar';
import { useIntentionalSignOut } from '@/hooks/useIntentionalSignOut';

/**
 * UserMenu — topbar avatar trigger + dropdown.
 *
 * Hand-rolled (no @radix-ui/react-dropdown-menu in deps) so the menu has a
 * single dependency-free implementation that owns:
 *   - aria-expanded / aria-haspopup on the trigger
 *   - role="menu" + role="menuitem" on the panel
 *   - Escape closes the menu and returns focus to the trigger
 *   - Click outside closes the menu
 *   - Roving tab index inside the menu (ArrowUp/Down/Home/End)
 *   - First item gets focus when the menu opens; trigger regains focus on close
 *
 * Items are role-aware: Admin row only renders for admins. The "Switch role
 * for testing" row is intentionally omitted — the only existing impersonation
 * entry point requires picking a target user from /admin, which the Admin row
 * already exposes, so a duplicate menu entry would just be visual noise.
 */

interface UserMenuProps {
  displayName: string;
  /** Show the Admin entry (member managers, #2103). */
  showAdmin: boolean;
}

export function UserMenu({ displayName, showAdmin }: UserMenuProps) {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const signOut = useIntentionalSignOut(() => triggerRef.current?.focus());
  const menuRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  const close = useCallback(
    (returnFocus: boolean) => {
      setOpen(false);
      if (returnFocus) {
        // Return focus to the trigger when the menu is dismissed via keyboard
        // or by selecting an item, so screen-reader users land back on the
        // control they invoked.
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
    },
    [],
  );

  // Close on outside click. Bound only while the menu is open so we don't
  // pay for a global listener in the steady state.
  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: PointerEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close(false);
    }
    document.addEventListener('pointerdown', onDocPointer);
    return () => document.removeEventListener('pointerdown', onDocPointer);
  }, [open, close]);

  // Global Escape handler — only active while open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  // When the menu opens, move focus to the first item. When it closes, clear
  // any leftover refs so item indexing stays correct on the next open.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => itemsRef.current[0]?.focus());
    } else {
      itemsRef.current = [];
    }
  }, [open]);

  function focusItem(idx: number) {
    const items = itemsRef.current.filter(Boolean) as HTMLButtonElement[];
    if (items.length === 0) return;
    const next = ((idx % items.length) + items.length) % items.length;
    items[next]?.focus();
  }

  function onMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const items = itemsRef.current.filter(Boolean) as HTMLButtonElement[];
    const active = document.activeElement as HTMLButtonElement | null;
    const idx = active ? items.indexOf(active) : -1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusItem(idx + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusItem(idx - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusItem(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusItem(items.length - 1);
    } else if (e.key === 'Tab') {
      // Tab from inside the menu dismisses it (don't trap focus indefinitely;
      // the user explicitly asked for the next focusable element). Browser
      // handles where focus lands.
      close(false);
    }
  }

  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  }

  // Build the item list once per render so role gating is centralized and the
  // ref array stays in lockstep with what we render.
  type Item =
    | { kind: 'item'; label: string; onSelect: () => void; danger?: boolean }
    | { kind: 'separator' };

  const items: Item[] = [
    {
      kind: 'item',
      label: 'Profile',
      onSelect: () => {
        close(true);
        navigate('/settings');
      },
    },
    ...(showAdmin
      ? [
          {
            kind: 'item' as const,
            label: 'Admin',
            onSelect: () => {
              close(true);
              navigate('/admin');
            },
          },
        ]
      : []),
    { kind: 'separator' as const },
    {
      kind: 'item',
      label: 'Sign out',
      danger: true,
      onSelect: () => {
        close(false);
        void signOut.requestSignOut();
      },
    },
  ];

  // Walk the items list to assign menuitem indexes (separators don't count).
  let menuIdx = 0;

  return (
    <div className="ft-topbar__usermenu">
      {signOut.dialog}
      <button
        ref={triggerRef}
        type="button"
        className="ft-topbar__usermenu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Account menu for ${displayName}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
      >
        <Avatar
          name={displayName}
          size={28}
          className="ft-topbar__avatar"
          title={displayName}
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Account menu"
          className="ft-topbar__usermenu-panel"
          onKeyDown={onMenuKeyDown}
        >
          <div className="ft-topbar__usermenu-header" aria-hidden="true">
            <div className="ft-topbar__usermenu-name">{displayName}</div>
          </div>
          {items.map((it, i) => {
            if (it.kind === 'separator') {
              return (
                <div
                  key={`sep-${i}`}
                  role="separator"
                  className="ft-topbar__usermenu-sep"
                />
              );
            }
            const idx = menuIdx;
            menuIdx += 1;
            return (
              <button
                key={`item-${i}`}
                ref={(el) => {
                  itemsRef.current[idx] = el;
                }}
                type="button"
                role="menuitem"
                className={
                  it.danger
                    ? 'ft-topbar__usermenu-item ft-topbar__usermenu-item--danger'
                    : 'ft-topbar__usermenu-item'
                }
                onClick={() => {
                  void it.onSelect();
                }}
              >
                {it.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
